// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useRef, useState, useEffect } from 'react';
import '../styles/Sidebar.nav.css';
import * as styles from '../styles/Sidebar.styles';
import TheShelfMainLogo from '../assets/images/TheShelfMainLogo.svg';
import AccellorLogo from '../assets/images/accellor-logo.png';
import icHome from '../assets/images/ic_Home.svg';
import icPeople from '../assets/images/ic_people.svg';
import icPerformance from '../assets/images/ic_performance.svg';
import icToken from '../assets/images/ic_tokens.svg';
import icPhone from '../assets/images/ic_phone.svg';
import icFeedback from '../assets/images/ic_feedback.svg';
import { fetchConfig, AppConfigModel } from '../utils/AppUtils';
import { Link, useLocation } from 'react-router-dom';

export interface SidebarProps {
  userInitial?: string;
  onLogout?: () => void;
}

const CloseXIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const Sidebar = ({ userInitial = 'A', onLogout }: SidebarProps): JSX.Element => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [contactPopoverOpen, setContactPopoverOpen] = useState(false);
  const [config, setConfig] = useState<AppConfigModel | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isReportsActive = location.pathname.startsWith('/reports');

  useEffect(() => {
    const loadConfig = async (): Promise<void> => {
      try {
        const appConfig = await fetchConfig();
        setConfig(appConfig);
      } catch (error) {
        console.error('Error loading config for sidebar:', error);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
        setPopoverOpen(false);
      }
    };
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [popoverOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (contactRef.current && !contactRef.current.contains(event.target as Node)) {
        setContactPopoverOpen(false);
      }
    };
    if (contactPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contactPopoverOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setPopoverOpen(false);
        setContactPopoverOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = (): void => {
    setPopoverOpen(false);
    onLogout?.();
  };

  const handleAvatarKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPopoverOpen((open) => !open);
    }
  };

  const handleLogoutKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleLogout();
    }
  };

  console.log('CONSOLE', config?.agentBaseUrl);

  return (
    <aside
      style={{
        ...styles.sidebarRoot,
        ...(contactPopoverOpen ? styles.sidebarRootPopoverOpen : {})
      }}
    >
      <div style={styles.logoWrapper}>
        <img src={AccellorLogo} alt="Accellor Logo" width={40} height={40} />
      </div>
      <nav style={styles.navList}>
        <button
          style={styles.navItemActive}
          className="sidebar-nav-btn sidebar-nav-btn-active"
          title="Home"
          type="button"
        >
          <img src={icHome} alt="Home" width={22} height={22} />
        </button>
        <a
          href="/reports"
          target="_blank"
          rel="noopener noreferrer"
          style={isReportsActive ? styles.navItemActive : styles.navItemBase}
          className={`sidebar-nav-btn${isReportsActive ? ' sidebar-nav-btn-active' : ''}`}
          title="Reports / Performance"
          aria-label="Open Reports / Performance in a new tab"
        >
          <img src={icPerformance} alt="Reports / Performance" width={22} height={22} />
        </a>
        {/* <a
          href={config ? `${config.agentBaseUrl}/onboarding` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Onboarding"
          aria-label="Open Onboarding in new tab"
        >
          <img src={icPeople} alt="Onboarding" width={22} height={22} />
        </a>
        <a
          href={config ? `${config.agentBaseUrl}/performance` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Performance"
          aria-label="Open Performance in new tab"
        >
          <img src={icPerformance} alt="Performance" width={22} height={22} />
        </a>
        <a
          href={config ? `${config.agentBaseUrl}/tokenconsumption` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Token consumption"
          aria-label="Open Token consumption in new tab"
        >
          <img src={icToken} alt="Token consumption" width={22} height={22} />
        </a>
        <a
          href={config ? `${config.agentBaseUrl}/feedback` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.navItemBase}
          className="sidebar-nav-btn"
          title="Feedback"
          aria-label="Open Feedback in new tab"
        >
          <img src={icFeedback} alt="Feedback" width={22} height={22} />
        </a>
        <div style={styles.contactPopoverWrapper} ref={contactRef}>
          <button
            style={styles.navItemBase}
            className="sidebar-nav-btn"
            title="Contact"
            type="button"
            onClick={() => setContactPopoverOpen((open) => !open)}
            aria-expanded={contactPopoverOpen}
            aria-haspopup="dialog"
            aria-label="Contact for queries"
          >
            <img src={icPhone} alt="Contact" width={22} height={22} />
          </button>
          {contactPopoverOpen && (
            <div role="dialog" aria-labelledby="contact-popover-title" style={styles.contactPopover}>
              <button
                type="button"
                onClick={() => setContactPopoverOpen(false)}
                style={styles.contactPopoverClose}
                aria-label="Close contact popover"
              >
                <CloseXIcon />
              </button>
              <h2 id="contact-popover-title" style={styles.contactPopoverTitle}>
                Please contact below for any queries:
              </h2>
              <p style={styles.contactPopoverLine}>
                Khaled Al-Basti -{' '}
                <a href="mailto:khaled.basti@g42.ai" style={styles.contactPopoverMailto}>
                  khaled.basti@g42.ai
                </a>
              </p>
              <p style={{ ...styles.contactPopoverLine, marginBottom: 0 }}>
                Geet Bhanawat -{' '}
                <a href="mailto:geet.bhanawat@g42.ai" style={styles.contactPopoverMailto}>
                  geet.bhanawat@g42.ai
                </a>
              </p>
            </div>
          )}
        </div> */}
      </nav>
      <div style={styles.avatarWrapper} ref={avatarRef}>
        <button
          type="button"
          className="sidebar-avatar-btn"
          style={styles.avatarBottom}
          onClick={() => setPopoverOpen((open) => !open)}
          onKeyDown={handleAvatarKeyDown}
          aria-expanded={popoverOpen}
          aria-haspopup="menu"
          aria-label="User menu"
          title="User menu"
        >
          {userInitial.charAt(0).toUpperCase()}
        </button>
        {popoverOpen && (
          <div role="menu" style={styles.popover} aria-label="User menu">
            <button
              type="button"
              role="menuitem"
              className="sidebar-popover-item"
              style={styles.popoverItem}
              onClick={handleLogout}
              onKeyDown={handleLogoutKeyDown}
              tabIndex={0}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
